import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const cell = { padding: '8px 10px', borderBottom: '1px solid #e2e8f0', fontSize: 13, verticalAlign: 'top' };
const STATUS_COLORS = { Pending: '#b45309', Approved: '#15803d', Rejected: '#b91c1c', Revoked: '#64748b' };

export default function RegistrationAccessPage() {
    const { t, i18n } = useTranslation(['registrationAccess', 'common']);
    const [blocked, setBlocked] = useState(false);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgIsError, setMsgIsError] = useState(false);
    const [allowed, setAllowed] = useState({});
    const [exportFrom, setExportFrom] = useState('');
    const [exportTo, setExportTo] = useState('');
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi().get('/admin/registration-access');
            setBlocked(!!res.data.blocked);
            setApplications(res.data.applications ?? []);
        } catch {
            setMsg(t('registrationAccess:admin.loadError'));
            setMsgIsError(true);
        }
        setLoading(false);
    }, [t]);

    useEffect(() => { load(); }, [load]);

    function notify(text, isError) {
        setMsg(text);
        setMsgIsError(isError);
    }

    async function toggleBlocked(next) {
        setBusy(true);
        try {
            await adminApi().put('/admin/registration-access', { blocked: next });
            setBlocked(next);
            notify(t('registrationAccess:admin.saved'), false);
        } catch {
            notify(t('registrationAccess:admin.saveError'), true);
        }
        setBusy(false);
    }

    async function review(id, action) {
        setBusy(true);
        try {
            const body = action === 'approve' ? { registrationsAllowed: Number(allowed[id] || 3) } : {};
            await adminApi().post(`/admin/registration-access/applications/${id}/${action}`, body);
            await load();
            notify(t('registrationAccess:admin.saved'), false);
        } catch {
            notify(t('registrationAccess:admin.saveError'), true);
        }
        setBusy(false);
    }

    function saveBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    async function downloadCsv() {
        setExporting(true);
        try {
            const params = {};
            if (exportFrom) params.from = exportFrom;
            if (exportTo) params.to = exportTo;
            const res = await adminApi().get('/admin/registration-access/export.csv', { params, responseType: 'blob' });
            saveBlob(res.data, `tester-undertakings-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch {
            notify(t('registrationAccess:admin.exportError'), true);
        }
        setExporting(false);
    }

    // Текст обязательства той версии, которую видит заявитель (русская редакция), для приложения к выгрузке.
    function downloadUndertakingText() {
        const tr = (k) => t(`registrationAccess:gate.${k}`, { lng: 'ru' });
        const lines = [
            tr('undertakingTitle'),
            '',
            tr('undertakingIntro'),
            ...['u1', 'u2', 'u3', 'u4', 'u5'].map((k, i) => `${i + 1}. ${tr(k)}`),
            '',
            tr('undertakingNote'),
            '',
            `${tr('undertakingAccept')}`,
        ];
        saveBlob(new Blob([String.fromCharCode(0xFEFF) + lines.join(String.fromCharCode(13, 10))], { type: 'text/plain;charset=utf-8' }), 'tester-undertaking-v1.0.txt');
    }

    const fmt = (iso) => (iso ? new Date(iso).toLocaleString(i18n.language) : '');

    if (loading) {
        return (
            <OwnerPageLayout title={t('registrationAccess:admin.pageTitle')} maxWidth={1000}>
                <div className="sp-loading-page">{t('common:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout title={t('registrationAccess:admin.pageTitle')} maxWidth={1000}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>{t('registrationAccess:admin.subtitle')}</p>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{t('registrationAccess:admin.perServerNote')}</p>

            {msg && (
                <p style={{
                    marginBottom: 12, fontSize: 13, padding: '10px 14px', borderRadius: 8,
                    background: msgIsError ? '#fdecea' : '#e8f5e9', color: msgIsError ? '#c0392b' : '#2e7d32',
                }}>
                    {msg}
                </p>
            )}

            <div className="sp-panel" style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={blocked}
                        disabled={busy}
                        onChange={e => toggleBlocked(e.target.checked)}
                    />
                    {t('registrationAccess:admin.blockedLabel')}
                </label>
                <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0 0' }}>{t('registrationAccess:admin.blockedHint')}</p>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '10px 0 0', color: blocked ? '#b91c1c' : '#15803d' }}>
                    {blocked ? t('registrationAccess:admin.blockedOn') : t('registrationAccess:admin.blockedOff')}
                </p>
            </div>

            <div className="sp-panel" style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 4px' }}>{t('registrationAccess:admin.exportTitle')}</h3>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>{t('registrationAccess:admin.exportHint')}</p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <label style={{ fontSize: 12, color: '#64748b' }}>
                        {t('registrationAccess:admin.exportFrom')}
                        <input type="date" className="sp-input" style={{ display: 'block' }} value={exportFrom}
                            onChange={e => setExportFrom(e.target.value)} />
                    </label>
                    <label style={{ fontSize: 12, color: '#64748b' }}>
                        {t('registrationAccess:admin.exportTo')}
                        <input type="date" className="sp-input" style={{ display: 'block' }} value={exportTo}
                            onChange={e => setExportTo(e.target.value)} />
                    </label>
                    <button type="button" className="sp-btn sp-btn--primary" disabled={exporting} onClick={downloadCsv}>
                        {exporting ? t('registrationAccess:admin.exporting') : t('registrationAccess:admin.exportCsv')}
                    </button>
                    <button type="button" className="sp-btn sp-btn--ghost" onClick={downloadUndertakingText}>
                        {t('registrationAccess:admin.exportText')}
                    </button>
                </div>
            </div>

            <div className="sp-panel">
                <h3 style={{ margin: '0 0 4px' }}>{t('registrationAccess:admin.applications')}</h3>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>{t('registrationAccess:admin.wipeNote')}</p>

                {applications.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#64748b' }}>{t('registrationAccess:admin.empty')}</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                            <thead>
                                <tr style={{ textAlign: 'left', fontSize: 12, color: '#64748b' }}>
                                    <th style={cell}>{t('registrationAccess:admin.nickname')}</th>
                                    <th style={cell}>{t('registrationAccess:admin.comment')}</th>
                                    <th style={cell}>{t('registrationAccess:admin.created')}</th>
                                    <th style={cell}>{t('registrationAccess:admin.status')}</th>
                                    <th style={cell}>{t('registrationAccess:admin.accounts')}</th>
                                    <th style={cell}>{t('registrationAccess:admin.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applications.map(a => (
                                    <tr key={a.id}>
                                        <td style={{ ...cell, fontWeight: 600 }}>{a.nickname}</td>
                                        <td style={{ ...cell, maxWidth: 240, color: '#475569' }}>{a.comment}</td>
                                        <td style={cell}>{fmt(a.createdAt)}</td>
                                        <td style={{ ...cell, color: STATUS_COLORS[a.status], fontWeight: 600 }}>
                                            {t(`registrationAccess:admin.statuses.${a.status}`, { defaultValue: a.status })}
                                        </td>
                                        <td style={cell}>
                                            {a.registrationsUsed} / {a.registrationsAllowed}
                                            {a.registeredUserIds && (
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                                    {t('registrationAccess:admin.accountIds')}: {a.registeredUserIds}
                                                </div>
                                            )}
                                        </td>
                                        <td style={cell}>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                                {(a.status === 'Pending' || a.status === 'Rejected' || a.status === 'Revoked') && (
                                                    <>
                                                        <input
                                                            type="number" min={1} max={20}
                                                            title={t('registrationAccess:admin.accountsAllowed')}
                                                            value={allowed[a.id] ?? a.registrationsAllowed}
                                                            onChange={e => setAllowed(prev => ({ ...prev, [a.id]: e.target.value }))}
                                                            style={{ width: 54, padding: '4px 6px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                                                        />
                                                        <button type="button" className="sp-btn sp-btn--primary" disabled={busy}
                                                            onClick={() => review(a.id, 'approve')}>
                                                            {t('registrationAccess:admin.approve')}
                                                        </button>
                                                    </>
                                                )}
                                                {a.status === 'Pending' && (
                                                    <button type="button" className="sp-btn sp-btn--ghost" disabled={busy}
                                                        onClick={() => review(a.id, 'reject')}>
                                                        {t('registrationAccess:admin.reject')}
                                                    </button>
                                                )}
                                                {a.status === 'Approved' && (
                                                    <button type="button" className="sp-btn sp-btn--ghost" disabled={busy}
                                                        onClick={() => review(a.id, 'revoke')}>
                                                        {t('registrationAccess:admin.revoke')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </OwnerPageLayout>
    );
}
